import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  MessageCircle, 
  Send, 
  X, 
  Bot,
  User,
  Minimize2,
  Maximize2
} from "lucide-react";

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const ChatAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! I'm your Elite Edge Management Consultancy assistant. Which service are you looking for?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const services = {
    "trade license": {
      name: "Trade License Registration",
      link: "/services#trade-license",
      description: "Complete assistance with trade license applications and renewals"
    },
    "business setup": {
      name: "Business Setup",
      link: "/services#business-setup", 
      description: "End-to-end business establishment and VAT application support"
    },
    "sports management": {
      name: "Sports Management Consultancy",
      link: "/services#sports-management",
      description: "Comprehensive sports club facilities management and consultancy"
    },
    "event management": {
      name: "Event Management", 
      link: "/services#event-management",
      description: "Professional event planning and management services"
    },
    "hr services": {
      name: "HR & Compliance Services",
      link: "/services#hr-compliance",
      description: "Comprehensive UAE-compliant HR solutions and organizational development"
    }
  };

  const getResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase();
    
    // Check for greetings
    if (message.includes("hello") || message.includes("hi") || message.includes("hey")) {
      return "Hello! Welcome to Elite Edge Management Consultancy. Which service are you looking for?\n\n🏢 Trade License Registration\n🏗️ Business Setup\n🏆 Sports Management\n🎉 Event Management\n👥 HR & Compliance Services\n\nJust tell me which service interests you!";
    }

    // Check for service matches
    for (const [key, service] of Object.entries(services)) {
      if (message.includes(key) || message.includes(service.name.toLowerCase())) {
        return `Great! You're interested in **${service.name}**.\n\n📋 **${service.description}**\n\n🔗 **View Full Details:** Click here to see all subservices → ${service.link}\n\n📧 **Need More Info?** Email us at: info@eliteedgemc.ae\n\nOn our services page, you'll find complete details about all subservices under ${service.name}.`;
      }
    }

    // Check for general service inquiry
    if (message.includes("service") || message.includes("what do you offer")) {
      return "We offer the following services:\n\n🏢 **Trade License Registration** - License applications & renewals\n🏗️ **Business Setup** - Company formation & VAT registration\n🏆 **Sports Management** - Sports club setup & NOC support\n🎉 **Event Management** - Event planning & permits\n👥 **HR & Compliance** - UAE-compliant HR solutions\n\n📋 **View All Services:** /services\n📧 **Email Us:** info@eliteedgemc.ae\n\nWhich service interests you?";
    }

    // Check for contact/email requests
    if (message.includes("contact") || message.includes("email") || message.includes("more info")) {
      return "📧 **Contact Information:**\n\nEmail: info@eliteedgemc.ae\n\nFor detailed information about any of our services, please email us and our team will provide comprehensive details and answer all your questions.\n\n🔗 **Or Browse Services:** /services\n\nIs there a specific service you'd like to know more about?";
    }

    // Default response
    return "I can help you find the right service! We offer:\n\n🏢 Trade License Registration\n🏗️ Business Setup\n🏆 Sports Management\n🎉 Event Management\n👥 HR & Compliance Services\n\n📋 **View All Details:** /services\n📧 **Email Us:** info@eliteedgemc.ae\n\nWhich service are you looking for?";
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getResponse(inputMessage),
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const formatMessage = (text: string) => {
    // Convert service links to clickable links
    const linkRegex = /(\/services[#\w-]*)/g;
    const emailRegex = /(info@eliteedgemc\.ae)/g;
    
    let formattedText = text;
    
    // Replace service links
    formattedText = formattedText.replace(linkRegex, (match) => {
      return `<a href="${match}" class="text-blue-600 hover:text-blue-800 font-semibold underline" target="_blank">${match}</a>`;
    });
    
    // Replace email
    formattedText = formattedText.replace(emailRegex, (match) => {
      return `<a href="mailto:${match}" class="text-blue-600 hover:text-blue-800 font-semibold underline">${match}</a>`;
    });
    
    return <div dangerouslySetInnerHTML={{ __html: formattedText.replace(/\n/g, '<br>') }} />;
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg z-50"
        size="icon"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <Card className={`fixed bottom-6 right-6 w-96 shadow-2xl z-50 transition-all duration-300 ${
      isMinimized ? 'h-16' : 'h-[500px]'
    }`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-primary text-primary-foreground rounded-t-lg">
        <div className="flex items-center space-x-2">
          <Bot className="h-5 w-5" />
          <CardTitle className="text-sm font-medium">Elite Edge Assistant</CardTitle>
          <Badge variant="secondary" className="text-xs">Online</Badge>
        </div>
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-primary-foreground hover:bg-primary-foreground/20"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <Maximize2 className="h-3 w-3" /> : <Minimize2 className="h-3 w-3" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-primary-foreground hover:bg-primary-foreground/20"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      
      {!isMinimized && (
        <CardContent className="flex flex-col h-[calc(500px-80px)] p-0">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    message.sender === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <div className="flex items-start space-x-2">
                    {message.sender === 'bot' && <Bot className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                    {message.sender === 'user' && <User className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                    <div className="whitespace-pre-wrap">
                      {message.sender === 'bot' ? formatMessage(message.text) : message.text}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-3 py-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Bot className="h-4 w-4" />
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="border-t p-4">
            <div className="flex space-x-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Which service are you looking for?"
                className="flex-1"
              />
              <Button onClick={handleSendMessage} size="icon">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ChatAgent;